import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ApiClient from '../../../state/api/ApiClient';
import PromiseBuilder from '../../../utils/PromiseUtils';
import Input from '@result/result-ui-components/src/components/Input';
import Button from '@result/result-ui-components/src/components/Button';
import Select from '@result/result-ui-components/src/components/Select';
import TextArea from '@result/result-ui-components/src/components/TextArea';
import { useForm } from '@result/result-ui-components/src/hooks/form/formHook';
import * as yup from 'yup';
import FormContainer from '../../widget/FormContainer';
import InformationInput from '../../widget/InformationInput';
import slovene from '../../../translations/slovene';
import { useDispatch, useSelector } from 'react-redux';
import {
	deleteIllegalDumpingLocation,
	fetchActualLandUses,
	fetchCadastralMunicipalities,
	fetchDedicatedLandUses,
	fetchIllegalWasteDumpsDetails,
	fetchMunicipalities,
	fetchOwnershipTypes,
} from '../../../state/api/illegalWasteDumpsAPI';
import { dateToString } from '@result/result-ui-components/src/utils/DateUtils';
import WasteTypeInput from '../../widget/WasteTypeInput';
import unitString from '../../../utils/unitUtil';
import MapComponent from '../../widget/MapComponent';
import PhotoGallery from '../../widget/PhotoGallery';
import {
	addErrorNotification,
	addSuccessNotification,
	addWarningNotification,
} from '../../../state/notification/notifications';
import InputTag from '../../widget/InputTag';
import { PlotContext } from '../../../context/PlotContext';
import { fetchWasteCategoriesPage } from '../../../state/api/wasteCategoriesAPI';
import Modal from "../../widget/Modal";
import {rotateImageFile} from "../../../utils/photoUtils";
import apiClient from "../../../state/api/ApiClient";

function IllegalWasteDumpsListForm() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams();
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const data = useSelector((state) => state.illegalWasteDumpsAPI.details);
	const [photos, setPhotos] = useState([]);
	const [isWasteTypesValid, setIsWasteTypesValid] = useState(false);
	const { plot, setPlot } = useContext(PlotContext);
	const listCategories = useSelector((state) => state.wasteCategoriesAPI.list);
	const [isDeleteLocationModalShown, setIsDeleteLocationModalShown] = useState(false);
	const [photoRotations, setPhotoRotations] = useState({});

	useEffect(() => {
		if (id) {
			dispatch(fetchIllegalWasteDumpsDetails(id));
		} else {
			getField('status').setValue('NEW');
		}
		dispatch(fetchWasteCategoriesPage());

		return () => {
			setPlot(null);
		};
	}, []);

	useEffect(() => {
		if (id) {
			setValues(data);
			setPhotos(data?.photos);
		}
	}, [data]);

	useEffect(() => {
		if (plot) {
			setValues((prev) => {
				return { ...prev, ...plot };
			});
		}
	}, [plot]);

	const statusOptions = Object.keys(slovene.waste_dump_type_options).map(
		(key) => {
			return {
				id: key,
				type: key,
				label: slovene.waste_dump_type_options[key],
			};
		}
	);

	const schema = {
		status: yup.string(),

		x: yup.string().nullable(),
		y: yup.string().nullable(),
		longitude: yup.string().required(),
		latitude: yup.string().required(),

		wasteTypes: yup.array().nullable(),

		area: yup.number().nullable(),
		volume: yup.number().nullable(),

		images: yup.array().nullable(),

		comment: yup.string().max(2500).nullable(),
	};

	const createToast = (type = 'success') => {
		if (type === 'success') {
			dispatch(
				addSuccessNotification({
					title: t('update_success_title'),
					content: t('update_success_content'),
				})
			);
		} else if (type === 'warning') {
			dispatch(
				addWarningNotification({
					title: t('update_warning_title'),
					content: t('update_warning_content'),
				})
			);
		} else {
			dispatch(
				addErrorNotification({
					title: t('update_fail_title'),
					content: t('update_fail_content'),
				})
			);
		}
	};

	const submit = () => {
		if (!isWasteTypesValid) return;
		setIsLoading(true);

		let ownershipTypes = values.ownershipTypes || [];
		let actualLandUses = values.actualLandUses || [];
		let dedicatedLandUses = values.dedicatedLandUses || [];

		if (!id) {
			ownershipTypes = ownershipTypes.map(({ code }) => code);
			actualLandUses = actualLandUses.map(({ code }) => code);
			dedicatedLandUses = dedicatedLandUses.map(({ code }) => code);
		}

		const requestData = {
			id,
			status: values.status || 'NEW',

			municipality: values.municipality?.name || '',
			cadastralMunicipality: values.cadastralMunicipality?.name || '',
			municipalityCode: values.municipality?.code || '',
			cadastralMunicipalityCode: values.cadastralMunicipality?.code || '',

			ownerWaste: values.ownerWaste,
			causedBy: values.causedBy,
			ownerLand: values.ownerLand,
			ownershipTypes,
			actualLandUses,
			dedicatedLandUses,

			wasteTypes: values.wasteTypes || [],

			longitude: values.longitude,
			latitude: values.latitude,

			area: values.area || 0,
			volume: values.volume || 0,

			comment: values.comment,
		};

		const request = async () =>
			id
				? ApiClient.put('/api/illegal-dumping-location/web/' + id, requestData)
				: ApiClient.post('/api/illegal-dumping-location/web', requestData);

		let invalid = false;

		new PromiseBuilder()
			.then(request)
			.then(async (result) => {
				const entryId = id || result?.data?.id;

				const photoIdsToDelete = values.deletePhotos ?? [];
				const newPhotoRotations =  Object.fromEntries(Object.entries(photoRotations).filter(
					([key]) => !photoIdsToDelete.includes(key) && !key.startsWith("blob:")));
				if(Object.entries(newPhotoRotations).length > 0) {
					for (const [key, value] of Object.entries(newPhotoRotations)) {
						const photoToRotate = data.photos.find(photo => photo.id === key);
						if(photoToRotate == null || value === 0) {
							continue;
						}

						new PromiseBuilder()
							.then(async () => {
								const imageResponse = await apiClient.getFile(`/api/illegal-dumping-location/photo/${photoToRotate.id}`, false);
								const blob = imageResponse.data;
								let file = new File([blob], photoToRotate.fileName, {type: blob.type});
								file = await rotateImageFile(file, (value ?? 0) * 90);
								const formData = new FormData();
								formData.append('photo', file);
								await ApiClient.post(
									'/api/illegal-dumping-location/' + entryId + '/photo/' + key,
									formData
								);
							})
							.then(() => {})
							.catch(async (err) => {
								setIsLoading(false);
								createToast('warning');
								navigate('/' + t('navigation.href.illegal_waste_dumps_list'));
							})
							.run();
					}
				}

				if (values?.photos?.length > 0) {
					values.photos.forEach((photo) => {
						if (typeof photo === 'string') return;
						invalid = true;
						const request = async () =>
							ApiClient.post(
								'/api/illegal-dumping-location/' + entryId + '/photo',
								photo
							);

						new PromiseBuilder()
							.then(request)
							.then(async () => {
								createToast('success');
								invalid = false;
								setFormNotDirty();
								setIsLoading(false);
								dispatch(fetchIllegalWasteDumpsDetails(entryId));
								navigate('/' + t('navigation.href.illegal_waste_dumps_list'));
							})
							.then(() => {})
							.catch(async (err) => {
								setIsLoading(false);
								createToast('warning');
								navigate('/' + t('navigation.href.illegal_waste_dumps_list'));
							})
							.run();
					});
				}

				if (values?.deletePhotos && values.deletePhotos.length > 0) {
					values.deletePhotos.forEach((photo) => {
						invalid = true;
						const request = async () =>
							ApiClient.delete('/api/illegal-dumping-location/photo/' + photo);

						new PromiseBuilder()
							.then(request)
							.then(async (data) => {
								createToast('success');
								invalid = false;
								setFormNotDirty();
								setIsLoading(false);
								dispatch(fetchIllegalWasteDumpsDetails(entryId));
								navigate('/' + t('navigation.href.illegal_waste_dumps_list'));
							})
							.then(() => {})
							.catch(async (err) => {
								setIsLoading(false);
								createToast('warning');
								navigate('/' + t('navigation.href.illegal_waste_dumps_list'));
							})
							.run();
					});
				}

				setFormNotDirty();
				setIsLoading(false);
			})
			.then(() => {
				if (!invalid) {
					createToast('success');
					if (id) {
						dispatch(fetchIllegalWasteDumpsDetails(id));
					}
					setPlot(null);
					navigate('/' + t('navigation.href.illegal_waste_dumps_list'));
				}
			})
			.catch(async (err) => {
				createToast('error');
				setIsLoading(false);
			})
			.run();
	};

	const {
		values,
		getField,
		setFormNotDirty,
		handleSubmit,
		setValues,
		isEdited,
	} = useForm(submit, {}, schema);

	return (
		<>
			<Link
				to={t('navigation.href.history_change')}
				className="button is-link button-history">
				{t('navigation.label.history_change')}
				<i className="icon fz-arrow-right" />
			</Link>
			<FormContainer onSubmit={handleSubmit} isEdited={isEdited}>
				{id && (
					<div className="card">
						<header className="card-header">
							<p className="card-header-title">
								<span className="icon fz-circle-info" />
								<span>{t('general_info')}</span>
							</p>
						</header>
						<div className="card-content">
							<div className="columns flex-wrap">
								<div className="column is-flex is-align-items-center">
									<InformationInput label={t('insert_by')}>
										{values?.insertBy}
									</InformationInput>
								</div>
								<div className="column is-flex is-align-items-center">
									<InformationInput label={t('insert_at')}>
										{dateToString(values?.insertAt, true, true)}
									</InformationInput>
								</div>
								<div className="column is-flex is-align-items-center">
									<InformationInput label={t('update_at')}>
										{dateToString(values?.updateAt, false, true)}
									</InformationInput>
								</div>
								<div className="column">
									<InformationInput label={t('status')}>
										<Select
											options={statusOptions}
											optionsLabel={'label'}
											optionsCode={'id'}
											optionsValue={'type'}
											formField={getField('status')}
											className={`waste-status is-${getField('status').value}`}
										/>
									</InformationInput>
								</div>
							</div>
						</div>
					</div>
				)}

				<div className="card">
					<header className="card-header">
						<p className="card-header-title">
							<span className="icon fz-pin" />
							<span>{t('location')}</span>
						</p>
					</header>
					<div className="card-content">
						<div className="columns flex-wrap">
							<div className="column is-12-tablet is-one-fifth-fullhd">
								<InformationInput label="X" margin={true} isGrid={true}>
									{getField('x').value}
								</InformationInput>
								<InformationInput label="Y" margin={true} isGrid={true}>
									{getField('y').value}
								</InformationInput>
								<InformationInput
									label={t('lon')}
									margin={true}
									isGrid={true}
									error={getField('longitude').error}>
									{getField('longitude').value}
								</InformationInput>
								<InformationInput
									label={t('lat')}
									margin={true}
									isGrid={true}
									error={getField('latitude').error}>
									{getField('latitude').value}
								</InformationInput>
							</div>
							<div className="column is-6-tablet is-one-third-fullhd">
								<InputTag
									label={t('municipality')}
									formField={getField('municipality')}
									id="municipality"
									selector={[
										(state) => state.illegalWasteDumpsAPI.municipalities,
									]}
									fetchAction={[fetchMunicipalities]}
									valueType="object"
									multiselect={false}
									isFilter={false}
								/>
								<Input
									label={t('municipality')}
									formField={getField('municipality.code')}
									disabled={true}
									margin={true}
								/>
							</div>
							<div className="column is-6-tablet is-one-third-fullhd">
								<InputTag
									label={t('cadastral_municipality')}
									formField={getField('cadastralMunicipality')}
									id="cadastralMunicipality"
									selector={[
										(state) =>
											state.illegalWasteDumpsAPI.cadastralMunicipalities,
									]}
									fetchAction={[fetchCadastralMunicipalities]}
									valueType="object"
									multiselect={false}
									isFilter={false}
								/>
								<Input
									label={t('cadastral_municipality_code')}
									formField={getField('cadastralMunicipality.code')}
									disabled={true}
									margin={true}
								/>
							</div>
						</div>
						<MapComponent
							hasSearch={id ? false : true}
							hasCenter={false}
							initialZoom={18}
							isFull={false}
							hasPopup={false}
							markers={[
								{
									latitude: values?.latitude,
									longitude: values?.longitude,
									status: values?.status,
								},
							]}
							centerMarker={true}
							edit={id ? true : false}
						/>
					</div>
				</div>

				<div className="card">
					<header className="card-header">
						<p className="card-header-title">
							<span className="icon fz-user" />
							<span>{t('legal_status')}</span>
						</p>
					</header>
					<div className="card-content">
						<div className="columns flex-wrap">
							<div className="column is-6-tablet is-one-third-fullhd">
								<Input
									label={t('waste_dump_owner')}
									formField={getField('ownerWaste')}
								/>
								<InputTag
									label={t('ownership_type')}
									formField={getField('ownershipTypes')}
									id="ownershipTypes"
									selector={[
										(state) => state.illegalWasteDumpsAPI.ownershipTypes,
									]}
									fetchAction={[fetchOwnershipTypes]}
									valueType="object"
									multiselect={true}
									isFilter={false}
								/>
							</div>
							<div className="column is-6-tablet is-one-third-fullhd">
								<Input
									label={t('responsible_person')}
									formField={getField('causedBy')}
								/>
								<InputTag
									label={t('actual_land_use')}
									formField={getField('actualLandUses')}
									id="actualLandUses"
									selector={[
										(state) => state.illegalWasteDumpsAPI.actualLandUses,
									]}
									fetchAction={[fetchActualLandUses]}
									valueType="object"
									multiselect={true}
									isFilter={false}
								/>
							</div>
							<div className="column is-6-tablet is-one-third-fullhd">
								<Input
									label={t('land_owner')}
									formField={getField('ownerLand')}
								/>
								<InputTag
									label={t('dedicated_land_use')}
									formField={getField('dedicatedLandUses')}
									id="dedicatedLandUses"
									selector={[
										(state) => state.illegalWasteDumpsAPI.dedicatedLandUses,
									]}
									fetchAction={[fetchDedicatedLandUses]}
									valueType="object"
									multiselect={true}
									isFilter={false}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className="card">
					<header className="card-header">
						<p className="card-header-title">
							<span className="icon fz-box" />
							<span>{t('size_of_waste_dump')}</span>
						</p>
					</header>
					<div className="card-content">
						<div className="columns">
							<div className="column is-one-third">
								<Input
									unit={unitString('area')}
									type="number"
									label={t('area')}
									formField={getField('area')}
								/>
							</div>
							<div className="column is-one-third">
								<Input
									unit={unitString('volume')}
									type="number"
									label={t('volume')}
									formField={getField('volume')}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className="card">
					<header className="card-header">
						<p className="card-header-title">
							<span className="icon fz-sack" />
							<span>{t('waste_types')}</span>
						</p>
					</header>
					<div className="card-content">
						<WasteTypeInput
							categories={listCategories}
							formField={getField('wasteTypes')}
							isValid={(value) => setIsWasteTypesValid(value)}
						/>
					</div>
				</div>

				<div className="card">
					<header className="card-header">
						<p className="card-header-title">
							<span className="icon fz-camera" />
							<span>{t('images')}</span>
						</p>
					</header>
					<div className="card-content">
						<PhotoGallery
							id={id}
							initialPhotos={photos}
							formField={getField('photos')}
							deletePhoto={(photo) => {
								const photos = getField('deletePhotos').value;
								if (photos?.length > 0) {
									getField('deletePhotos').setValue([...photos, photo]);
								} else {
									getField('deletePhotos').setValue([photo]);
								}
							}}
							photoRotations={photoRotations}
							setPhotoRotations={setPhotoRotations}
						/>
					</div>
				</div>
				<div className="card">
					<header className="card-header">
						<p className="card-header-title">
							<span className="icon fz-pencil" />
							<span>{t('comment')}</span>
						</p>
					</header>
					<div className="card-content">
						<TextArea label="" rows={7} formField={getField('comment')} />
					</div>
				</div>

				<div className="form-footer">
					{id != null &&
						<Button
							label={t('delete_location')}
							color="danger"
							onClick={() =>
								setIsDeleteLocationModalShown(true)
							}
						/>
					}

					<Button
						label={t('cancel')}
						onClick={() =>
							navigate('/' + t('navigation.href.illegal_waste_dumps_list'))
						}
					/>
					{!id && (
						<Button
							isSubmit={true}
							color="info"
							label={t('save_draft')}
							isLoading={isLoading}
							onClick={() => {
								getField('status').setValue('DRAFT');
							}}
						/>
					)}
					<Button
						isSubmit={true}
						color="primary"
						label={t('save')}
						isLoading={isLoading}
						onClick={() => {
							if (getField('status').value === 'DRAFT' && !id)
								getField('status').setValue('NEW');
						}}
					/>
				</div>
			</FormContainer>
			<DeleteIllegalDumpingLocationModal
				t={t}
				isShown={isDeleteLocationModalShown}
				onCancel={() => setIsDeleteLocationModalShown(false)}
				isLoading={isLoading}
				onConfirmDeletion={() => {
					setIsLoading(true);
					dispatch(deleteIllegalDumpingLocation({illegalWasteDumpsId: id})).unwrap().then(() => {
						setFormNotDirty();
						setIsLoading(false);
						dispatch(addSuccessNotification({title: t('delete_location_title'), content: t('delete_location_success')}))
						navigate('/' + t('navigation.href.illegal_waste_dumps_list'));
					}).catch(() => {
						setIsLoading(false);
						dispatch(addErrorNotification({title: t('delete_location_title'), content: t('delete_location_error')}))
					});
				}}
			/>
		</>
	);
}

const DeleteIllegalDumpingLocationModal =
	({
		 t,
		 isShown = false,
		 onConfirmDeletion = () => {},
		 onCancel = () => {},
		 isLoading = false,
	 }
	) => {
	return (
		<Modal
			title={t('delete_location')}
			content={t('delete_location_message')}
			showPopup={isShown}
			label={t('confirmation_needed')}
			confirmButtonLabel={t('delete')}
			onConfirm={onConfirmDeletion}
			cancelButtonLabel={t('delete_cancel')}
			isLoading={false}
			onCancel={onCancel}></Modal>
	);
}

export default IllegalWasteDumpsListForm;
